import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import confetti from 'canvas-confetti';
import { Helmet } from 'react-helmet';
import { Heart, CloudLightning, LogIn, ArrowLeft, } from 'lucide-react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuthContext } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import { gradientText, gradientButton } from '../theme';
import type { Relation, RelationStatus } from '../types/relation';
import { useRelations } from '../hooks/useRelations';
import { useNotifications } from '../hooks/useNotifications';

// Interface pour les données retournées par get_relation_details
interface RelationDetails {
  relation: Relation & {
    target_photo?: string;
  };
  creator: {
    name: string;
    photo: string;
  };
}

function ShareConfirmation() {
  const { t } = useTranslation();
  const { relationId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuthContext();
  const { certifyRelation, rejectRelation } = useRelations();
  const { createNotification } = useNotifications();
  const [relationDetails, setRelationDetails] = useState<RelationDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [confirmed, setConfirmed] = useState<boolean | null>(null);

  useEffect(() => {
    loadRelationDetails();
  }, [relationId, user]);

  const loadRelationDetails = async () => {
    try {
      setLoading(true);
      setError(null);
      setConfirmed(null);

      console.log("Loading relation details for ID:", relationId);
      
      // Vérifier d'abord si la relation existe dans la table relations
      const { data: relationExists, error: checkError } = await supabase
        .from('relations')
        .select(`
          *,
          creator:profiles!relations_creator_id_fkey(
            id,
            full_name,
            avatar_url
          )
        `)
        .eq('id', relationId)
        .single();
        
      if (checkError) {
        console.error("Error checking if relation exists:", checkError);
        if (checkError.code === 'PGRST116') {
          console.error("Relation does not exist in the database");
          throw new Error(t('share.errors.relationNotFound'));
        }
      }
      
      if (!relationExists) {
        console.error("Relation not found in database");
        throw new Error(t('share.errors.relationNotFound'));
      }
      
      console.log("Relation exists in database:", relationExists);

      // Formater les données pour l'affichage
      const formattedDetails: RelationDetails = {
        relation: {
          id: relationExists.id,
          creator_id: relationExists.creator_id,
          target_photo: relationExists.photo || relationExists.target_photo,
          photo: relationExists.photo || relationExists.target_photo,
          target_name: relationExists.name || relationExists.target_name,
          name: relationExists.name || relationExists.target_name,
          target_phones: relationExists.phones || relationExists.target_phones || [],
          phones: relationExists.phones || relationExists.target_phones || [],
          target_country: relationExists.country || relationExists.target_country || undefined,
          country: relationExists.country || relationExists.target_country || undefined,
          target_city: relationExists.city || relationExists.target_city || undefined,
          city: relationExists.city || relationExists.target_city || undefined,
          status: relationExists.status as RelationStatus,
          confirmed: relationExists.confirmed,
          rejected: relationExists.rejected,
          created_at: relationExists.created_at,
          updated_at: relationExists.updated_at,
          certifier_id: relationExists.certifier_id || undefined,
          mirror_relation_id: relationExists.mirror_relation_id || undefined,
          deleted: relationExists.deleted
        },
        creator: {
          name: relationExists.creator?.full_name || relationExists.creator_name || t('profile.userProfile.defaultName'),
          photo: relationExists.creator?.avatar_url || relationExists.creator_avatar || '/default-avatar.png'
        }
      };

      console.log("Formatted relation details:", formattedDetails);
      
      setRelationDetails(formattedDetails);
      // Ne montrer la page de confirmation que si la relation est confirmée
      // Si elle est rejetée, on permet de voir à nouveau les options
      if (relationExists.confirmed) {
        setConfirmed(true);
      }
    } catch (err: any) {
      console.error("Error in loadRelationDetails:", err);
      setError(err.message || t('share.errors.loadingError'));
    } finally {
      setLoading(false);
    }
  };

  // Récupérer les informations du créateur du localStorage si disponibles
  useEffect(() => {
    if (relationId) {
      // Vérifier si nous venons de nous connecter en vérifiant les données sauvegardées
      const storedCreatorLogin = localStorage.getItem(`relation_${relationId}_creator_login`);
      const storedDetailsLogin = localStorage.getItem(`relation_${relationId}_details_login`);
      
      console.log("[ShareConfirmation] Checking stored data:", {
        storedCreatorLogin: !!storedCreatorLogin,
        storedDetailsLogin: !!storedDetailsLogin,
        user: !!user
      });
      
      if (storedCreatorLogin && storedDetailsLogin) {
        try {
          const creatorInfo = JSON.parse(storedCreatorLogin);
          const details = JSON.parse(storedDetailsLogin);
          
          console.log("[ShareConfirmation] Retrieved stored relation data:", { 
            creator: creatorInfo,
            relation: details.relation?.id
          });
          
          // Utiliser ces détails immédiatement pour un affichage rapide
          setRelationDetails(details);
          setLoading(false);
          
          // Attendre un court instant pour que l'authentification soit complètement établie
          // puis charger les données fraîches depuis le serveur
          setTimeout(() => {
            console.log("[ShareConfirmation] Loading fresh relation details after delay");
            if (user) {
              loadRelationDetails();
            }
          }, 2000);
          
          return;
        } catch (e) {
          console.error("[ShareConfirmation] Error parsing stored relation details:", e);
        }
      }
      
      // Si pas de données stockées ou erreur lors de la récupération, charger normalement
      loadRelationDetails();
    }
  }, [relationId, user]);

  const handleConfirm = async () => {
    if (!user || !relationDetails || !relationId) return;

    try {
      setLoading(true);
      setError(null);

      const { error: certifyError } = await certifyRelation(relationId, user.id);

      if (certifyError) {
        console.error("Error certifying relation:", certifyError);
        setError(t('share.errors.certificationError'));
        setLoading(false);
        return;
      }

      // Nettoyer les données de redirection maintenant que la certification est terminée
      localStorage.removeItem('pendingRelationId');
      localStorage.removeItem('redirectAfterAuth');
      localStorage.removeItem(`relation_${relationId}_creator_login`);
      localStorage.removeItem(`relation_${relationId}_details_login`);
      localStorage.removeItem(`relation_${relationId}_timestamp`);

      // Créer une notification pour le créateur si creator_id existe
      if (relationDetails.relation.creator_id) {
        await createNotification({
          userId: relationDetails.relation.creator_id,
          translationKey: 'relationConfirmed',
          translationParams: {
            name: relationDetails.relation.target_name || relationDetails.relation.name || 'quelqu\'un'
          }
        });
      }

      setConfirmed(true);
      
      // Lancer l'animation de confetti
      const duration = 15 * 1000;
      const animationEnd = Date.now() + duration;
      const defaults = { startVelocity: 30, spread: 360, ticks: 60, zIndex: 0 };

      function randomInRange(min: number, max: number) {
        return Math.random() * (max - min) + min;
      }

      const interval: any = setInterval(function() {
        const timeLeft = animationEnd - Date.now();

        if (timeLeft <= 0) {
          return clearInterval(interval);
        }

        const particleCount = 50 * (timeLeft / duration);
        
        confetti({
          ...defaults,
          particleCount,
          origin: { x: randomInRange(0.1, 0.3), y: Math.random() - 0.2 },
          colors: ['#ec4899', '#db2777', '#be185d']
        });
        confetti({
          ...defaults,
          particleCount,
          origin: { x: randomInRange(0.7, 0.9), y: Math.random() - 0.2 },
          colors: ['#ec4899', '#db2777', '#be185d']
        });
      }, 250);
    } catch (err) {
      console.error('Erreur lors de la certification:', err);
      setError(t('share.errors.certificationError'));
      setLoading(false);
    }
  };

  const handleReject = async () => {
    if (!user || !relationDetails || !relationId) return;

    try {
      setLoading(true);
      setError(null);

      const { error } = await rejectRelation(relationId, user.id);
      if (error) {
        setError(t('share.errors.rejectionError'));
        setLoading(false);
        return;
      }

      setConfirmed(false);
    } catch (err) {
      console.error('Erreur lors du rejet:', err);
      setError("Une erreur s'est produite lors du rejet");
      setLoading(false);
    }
  };

  const handleLogin = () => {
    // Stocker l'URL de la page actuelle pour la redirection après connexion
    const currentPath = `/share/${relationId}`;
    localStorage.setItem('redirectAfterAuth', currentPath);
    
    console.log("[ShareConfirmation] Storing redirect path:", currentPath);
    
    // Stocker l'ID de la relation explicitement pour la redirection
    if (relationId) {
      localStorage.setItem('pendingRelationId', relationId);
      console.log("[ShareConfirmation] Storing pending relation ID:", relationId);
    }
    
    // Marquer cet utilisateur comme nouvel utilisateur pour la redirection vers le flux d'inscription après certification
    localStorage.setItem('isNewUser', 'true');
    console.log("[ShareConfirmation] Marking user as new for SignUpFlow redirection");
    
    // Stocker les détails complets de la relation si disponibles
    if (relationDetails) {
      try {
        // Stocker les données avec des clés spécifiques à cette relation
        localStorage.setItem(`relation_${relationId}_creator_login`, JSON.stringify(relationDetails.creator));
        localStorage.setItem(`relation_${relationId}_details_login`, JSON.stringify(relationDetails));
        
        // Ajouter un timestamp pour la validité des données
        localStorage.setItem(`relation_${relationId}_timestamp`, Date.now().toString());
        
        console.log("[ShareConfirmation] Stored relation data for post-login retrieval");
      } catch (error) {
        console.error("[ShareConfirmation] Error storing relation details:", error);
      }
    }
    
    console.log("[ShareConfirmation] Navigating to login page");
    navigate('/login');
  };

  const handleBackToProfile = () => {
    // Nettoyer toutes les données de redirection avant de naviguer
    localStorage.removeItem('pendingRelationId');
    localStorage.removeItem('redirectAfterAuth');
    localStorage.removeItem(`relation_${relationId}_creator_login`);
    localStorage.removeItem(`relation_${relationId}_details_login`);
    localStorage.removeItem(`relation_${relationId}_timestamp`);
    
    // Vérifier si l'utilisateur est nouveau (s'est inscrit via le lien de partage)
    const isNewUser = localStorage.getItem('isNewUser') === 'true';
    
    // Rediriger vers le flux d'inscription si c'est un nouvel utilisateur,
    // sinon rediriger vers le profil
    if (isNewUser) {
      // Ajouter la relation certifiée/rejetée comme paramètre pour le flux d'inscription
      if (relationId) {
        localStorage.setItem('certified_relation_id', relationId);
      }
      window.location.replace(`${window.location.origin}/#/signup-flow`);
    } else {
      // Comportement existant pour les utilisateurs existants
      window.location.replace(`${window.location.origin}/#/profile`);
    }
  };

  if (loading && !relationDetails) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-primary-end border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-black flex flex-col items-center justify-center p-4">
        <div className="text-center max-w-md">
          <p className="text-red-500 text-lg mb-4">{error}</p>
          <Link
            to="/"
            className={`inline-flex items-center gap-2 ${gradientText} hover:opacity-80 transition-opacity`}
          >
            <ArrowLeft className="w-5 h-5" />
            {t('share.backToHome')}
          </Link>
        </div>
      </div>
    );
  }

  if (!relationDetails) return null;

  // Préparer les métadonnées pour le partage
  const metaTitle = `${relationDetails.creator.name} ${t('share.title', { status: relationDetails.relation.status === 'couple' ? t('profile.relations.status.en_couple') : t(`profile.relations.status.${relationDetails.relation.status}`) })}`;
  const metaDescription = t('share.question');
  const metaImage = relationDetails.creator.photo;

  return (
    <div className="min-h-screen bg-black text-white">
      <Helmet>
        <title>{metaTitle}</title>
        <meta name="description" content={metaDescription} />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:title" content={metaTitle} />
        <meta property="og:description" content={metaDescription} />
        <meta property="og:image" content={metaImage} />
        
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={metaTitle} />
        <meta name="twitter:description" content={metaDescription} />
        <meta name="twitter:image" content={metaImage} />
      </Helmet>
      
      <div className="max-w-2xl mx-auto px-4 py-8 sm:py-12">
        <div className="relative">
          <AnimatePresence>
            {confirmed === null && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                className="relative"
              >
                {/* Background gradient */}
                <div className="absolute inset-0 bg-gradient-to-b from-primary-start/20 via-transparent to-transparent rounded-3xl" />
                
                <div className="relative">
                  {/* Title */}
                  <div className="text-center mb-8">
                    <h1 className="text-4xl sm:text-4xl text-2xl font-bold mb-4">
                      {relationDetails.creator.name} {t('share.title', { status: relationDetails.relation.status === 'couple' ? t('profile.relations.status.en_couple') : t(`profile.relations.status.${relationDetails.relation.status}`) })}
                    </h1>
                    <p className={`text-lg sm:text-xl ${gradientText}`}>
                      {t('share.question')}
                    </p>
                  </div>

                  {/* Photos */}
                  <div className="relative flex justify-center items-center py-8 sm:py-12">
                    <div className="relative flex items-center justify-center gap-4">
                      {/* Left photo */}
                      <motion.div
                        initial={{ x: -50, opacity: 0 }}
                        animate={{ x: 0, opacity: 1 }}
                        transition={{ delay: 0.2 }}
                        className="relative"
                      >
                        <div className="w-32 h-32 sm:w-48 sm:h-48 relative rounded-3xl overflow-hidden transform -rotate-6">
                          <div className="absolute inset-0 bg-gradient-to-br from-primary-start to-primary-end p-[1px]">
                            <div className="absolute inset-0 bg-black">
                              <img
                                src={relationDetails.creator.photo}
                                alt={t('share.creatorAlt')}
                                className="w-full h-full object-cover"
                              />
                            </div>
                          </div>
                        </div>
                      </motion.div>

                      {/* Heart */}
                      <motion.div
                        initial={{ scale: 0 }}
                        animate={{ 
                          scale: [1, 1.2, 1],
                          filter: [
                            'drop-shadow(0 0 15px rgba(236,72,153,0.7))',
                            'drop-shadow(0 0 25px rgba(236,72,153,0.9))',
                            'drop-shadow(0 0 15px rgba(236,72,153,0.7))'
                          ]
                        }}
                        transition={{ 
                          delay: 0.4,
                          repeat: Infinity,
                          duration: 1.5,
                          ease: "easeInOut"
                        }}
                        className="absolute z-20"
                      >
                        <Heart className="w-12 h-12 sm:w-16 sm:h-16 text-pink-500 fill-pink-500" />
                      </motion.div>

                      {/* Right photo */}
                      <motion.div
                        initial={{ x: 50, opacity: 0 }}
                        animate={{ x: 0, opacity: 1 }}
                        transition={{ delay: 0.2 }}
                        className="relative"
                      >
                        <div className="w-32 h-32 sm:w-48 sm:h-48 relative rounded-3xl overflow-hidden transform rotate-6">
                          <div className="absolute inset-0 bg-gradient-to-bl from-primary-start to-primary-end p-[1px]">
                            <div className="absolute inset-0 bg-black">
                              <img
                                src={relationDetails.relation.target_photo}
                                alt={t('share.targetAlt')}
                                className="w-full h-full object-cover rounded-2xl sm:rounded-[40px]"
                                onError={(e) => {
                                  console.error("Erreur de chargement de l'image:", e);
                                  e.currentTarget.src = 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=400';
                                }}
                              />
                            </div>
                          </div>
                        </div>
                      </motion.div>
                    </div>
                  </div>

                  {/* Buttons */}
                  {user ? (
                    <div className="flex flex-col items-center">
                      <div className="flex gap-4 max-w-md w-full mx-auto mt-8">
                        <button
                          onClick={handleReject}
                          disabled={loading}
                          className="flex-1 bg-[#0A0A0A] hover:bg-[#0A0A0A]/80 text-white font-medium py-3 sm:py-4 px-4 sm:px-6 rounded-xl sm:rounded-2xl transition-colors border border-gray-800 text-base sm:text-lg disabled:opacity-50"
                        >
                          {t('share.reject')}
                        </button>
                        <button
                          onClick={handleConfirm}
                          disabled={loading}
                          className={`flex-1 ${gradientButton} text-white font-medium py-3 sm:py-4 px-4 sm:px-6 rounded-xl sm:rounded-2xl transition-colors text-base sm:text-lg disabled:opacity-50`}
                        >
                          {t('share.confirm')}
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="text-center max-w-md mx-auto mt-8">
                      <p className="text-gray-400 mb-4 text-xs">
                        {t('share.loginMessage')}
                      </p>
                      <button
                        onClick={handleLogin}
                        className={`w-full ${gradientButton} text-white font-medium py-4 px-6 rounded-2xl transition-colors flex items-center justify-center gap-2 text-lg`}
                      >
                        <LogIn className="w-6 h-6" />
                        {t('auth.login.submit')}
                      </button>
                    </div>
                  )}
                </div>
              </motion.div>
            )}

            {confirmed === true && (
              <motion.div
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                className="text-center py-8"
              >
                <motion.h1 
                  initial={{ y: -20, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.3 }}
                  className={`text-4xl sm:text-5xl font-bold ${gradientText} mb-4`}
                >
                  {t('share.relationCertified')}
                </motion.h1>
                
                <motion.p
                  initial={{ y: -20, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.5 }}
                  className="text-xl sm:text-2xl text-gray-400 mb-12"
                >
                  {t('share.congratulations')}
                </motion.p>

                <div className="relative mx-auto w-full max-w-[280px] sm:max-w-[400px] mb-16" style={{ aspectRatio: '1/1' }}>
                  {/* Decorative hearts */}
                  <motion.div
                    animate={{ 
                      scale: [1, 1.1, 1],
                      rotate: [0, 5, 0]
                    }}
                    transition={{
                      duration: 2,
                      repeat: Infinity,
                      ease: "easeInOut"
                    }}
                    className="absolute -top-6 -right-6 z-20"
                  >
                    <Heart className="w-12 h-12 sm:w-16 sm:h-16 text-primary-end fill-primary-end drop-shadow-[0_0_10px_rgba(236,72,153,0.5)]" />
                  </motion.div>
                  
                  <motion.div
                    animate={{ 
                      scale: [1, 1.1, 1],
                      rotate: [0, -5, 0]
                    }}
                    transition={{
                      duration: 2.5,
                      repeat: Infinity,
                      ease: "easeInOut"
                    }}
                    className="absolute -bottom-6 -left-6 z-20"
                  >
                    <Heart className="w-12 h-12 sm:w-16 sm:h-16 text-primary-start fill-primary-start drop-shadow-[0_0_10px_rgba(184,164,78,0.5)]" />
                  </motion.div>

                  {/* Main photo frame */}
                  <div className="relative w-full h-full">
                    {/* White frame border */}
                    <div className="absolute inset-0 bg-gradient-to-r from-white/20 to-white/30 rounded-2xl p-[2px]">
                      <div className="absolute inset-0 bg-black rounded-2xl">
                        {/* Photos container */}
                        <div className="flex h-full overflow-hidden rounded-2xl">
                          <div className="w-1/2 h-full relative">
                            <img
                              src={relationDetails.creator.photo}
                              alt={t('share.creatorAlt')}
                              className="absolute inset-0 w-full h-full object-cover grayscale"
                            />
                          </div>
                          <div className="w-1/2 h-full relative">
                            <img
                              src={relationDetails.relation.target_photo}
                              alt={t('share.targetAlt')}
                              className="absolute inset-0 w-full h-full object-cover grayscale"
                              onError={(e) => {
                                console.error("Erreur de chargement de l'image:", e);
                                e.currentTarget.src = 'https://i.ibb.co/TBxb9k8Z/Final-logo-BC-Couleur-sur-noir-00002.png';
                              }}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <motion.button
                  initial={{ y: 20, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.7 }}
                  onClick={handleBackToProfile}
                  className={`inline-flex items-center justify-center w-full gap-2 ${gradientText} hover:opacity-80 transition-opacity text-lg sm:text-xl`}
                >
                  <ArrowLeft className="w-5 h-5 sm:w-6 sm:h-6" />
                  {t('share.backToProfile')}
                </motion.button>
              </motion.div>
            )}

            {confirmed === false && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="text-center min-h-screen flex flex-col justify-between py-8"
              >
                <motion.h1 
                  initial={{ y: -20, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.3 }}
                  className={`text-4xl sm:text-5xl font-bold ${gradientText}`}
                >
                  {t('share.relationRejected')}
                </motion.h1>

                <div className="flex gap-8 sm:gap-24 items-center justify-center px-4">
                  <motion.div
                    animate={{ x: [-10, 10, -10], rotate: [-5, 5, -5] }}
                    transition={{ repeat: Infinity, duration: 0.5 }}
                  >
                    <div className="w-32 h-32 sm:w-64 sm:h-64 relative">
                      <div className="absolute inset-0 bg-gradient-to-r from-gray-700 to-gray-500 rounded-2xl sm:rounded-[40px] p-1 opacity-50">
                        <div className="absolute inset-0 bg-black rounded-2xl sm:rounded-[40px]">
                          <img
                            src={relationDetails.creator.photo}
                            alt={t('share.creatorAlt')}
                            className="w-full h-full object-cover rounded-2xl sm:rounded-[40px]"
                          />
                        </div>
                      </div>
                    </div>
                  </motion.div>
                  <CloudLightning className="w-12 h-12 sm:w-16 sm:h-16 text-yellow-500" />
                  <motion.div
                    animate={{ x: [10, -10, 10], rotate: [5, -5, 5] }}
                    transition={{ repeat: Infinity, duration: 0.5 }}
                  >
                    <div className="w-32 h-32 sm:w-64 sm:h-64 relative">
                      <div className="absolute inset-0 bg-gradient-to-r from-gray-700 to-gray-500 rounded-2xl sm:rounded-[40px] p-1 opacity-50">
                        <div className="absolute inset-0 bg-black rounded-2xl sm:rounded-[40px]">
                          <img
                            src={relationDetails.relation.target_photo}
                            alt={t('share.targetAlt')}
                            className="w-full h-full object-cover rounded-2xl sm:rounded-[40px]"
                            onError={(e) => {
                              console.error("Erreur de chargement de l'image:", e);
                              e.currentTarget.src = 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=400';
                            }}
                          />
                        </div>
                      </div>
                    </div>
                  </motion.div>
                </div>

                <motion.button
                  initial={{ y: 20, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.7 }}
                  onClick={handleBackToProfile}
                  className={`inline-flex items-center justify-center w-full gap-2 ${gradientText} hover:opacity-80 transition-opacity text-lg sm:text-xl`}
                >
                  <ArrowLeft className="w-5 h-5 sm:w-6 sm:h-6" />
                  {t('share.backToProfile')}
                </motion.button>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
}

export default ShareConfirmation;